import Highcharts from "highcharts"
import { HighchartsReact } from "highcharts-react-official"

import { getTailwindValue } from "~/utils/tailwind"

function normalPDF(x: number, mu: number, sigma: number): number {
  return (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mu) / sigma) ** 2)
}

/**
 * Bell curve (normal distribution) chart with three zones:
 * 1. Left of `mu`: muted ("base demand")
 * 2. `mu` to `threshold`: green ("safety stock zone")
 * 3. Right of `threshold`: red ("stockout zone")
 *
 * Typical use: visualising safety stock service level, highlighting that
 * the green area between μ and the threshold represents the safety buffer.
 */
export default function BellCurveChart({
  mu,
  sigma,
  threshold,
  safetyStockLabel,
}: {
  /** Mean of the distribution */
  mu: number
  /** Standard deviation — must be >= 1 to render meaningfully */
  sigma: number
  /** X-value where the safety stock zone ends (μ + bufferUnits) */
  threshold: number
  /** Label shown inside the green zone (e.g. "514 units"). Omit to show no label. */
  safetyStockLabel?: string
}) {
  const xMin = mu - 3.5 * sigma
  const xMax = mu + 4 * sigma
  const numPoints = 150
  const step = (xMax - xMin) / numPoints

  const allData = Array.from({ length: numPoints + 1 }, (_, i) => {
    const x = xMin + i * step
    return [x, normalPDF(x, mu, sigma)] as [number, number]
  })

  const greenColor = getTailwindValue("--color-green-500") || "#22c55e"
  const redColor = getTailwindValue("--color-red-400") || "#f87171"
  const mutedColor = getTailwindValue("--color-fg-muted") || "#888888"

  const options: Highcharts.Options = {
    chart: {
      type: "area",
      backgroundColor: "transparent",
      margin: [5, 5, 28, 5],
      height: 110,
      animation: false,
      style: { fontFamily: "inherit" },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: { enabled: false },
    xAxis: {
      type: "linear",
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
      plotLines: [
        {
          value: mu,
          dashStyle: "Dot",
          color: mutedColor,
          width: 1.5,
          zIndex: 5,
        },
        {
          value: threshold,
          dashStyle: "ShortDash",
          color: mutedColor,
          width: 1.5,
          zIndex: 5,
        },
      ],
      plotBands:
        safetyStockLabel != null
          ? [
              {
                from: mu,
                to: threshold,
                color: "transparent",
                label: {
                  text: safetyStockLabel,
                  style: { color: greenColor, fontSize: "10px", fontWeight: "600" },
                  align: "center",
                  verticalAlign: "top",
                  y: 8,
                },
              },
            ]
          : [],
    },
    yAxis: { visible: false, min: 0 },
    plotOptions: {
      area: {
        marker: { enabled: false },
        states: { hover: { enabled: false } },
        enableMouseTracking: false,
      },
    },
    series: [
      {
        type: "area",
        data: allData,
        lineWidth: 2,
        zoneAxis: "x",
        zones: [
          {
            value: mu,
            color: mutedColor,
            fillColor: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, Highcharts.color(mutedColor).setOpacity(0.35).get("rgba") as string],
                [1, Highcharts.color(mutedColor).setOpacity(0.04).get("rgba") as string],
              ],
            },
          },
          {
            value: threshold,
            color: greenColor,
            fillColor: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, Highcharts.color(greenColor).setOpacity(0.5).get("rgba") as string],
                [1, Highcharts.color(greenColor).setOpacity(0.06).get("rgba") as string],
              ],
            },
          },
          {
            color: redColor,
            fillColor: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, Highcharts.color(redColor).setOpacity(0.6).get("rgba") as string],
                [1, Highcharts.color(redColor).setOpacity(0.08).get("rgba") as string],
              ],
            },
          },
        ],
      } as Highcharts.SeriesAreaOptions,
    ],
  }

  return (
    <div>
      <HighchartsReact highcharts={Highcharts} options={options} />
      <div className="flex gap-3 justify-center -mt-1">
        <div className="flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ background: mutedColor, opacity: 0.5 }}
          />
          <span className="text-[10px] text-fg-muted">Base demand</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            className="w-2.5 h-2.5 rounded-sm"
            style={{ background: greenColor, opacity: 0.7 }}
          />
          <span className="text-[10px] text-fg-muted">Safety stock</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: redColor, opacity: 0.8 }} />
          <span className="text-[10px] text-fg-muted">Stockout zone</span>
        </div>
      </div>
    </div>
  )
}
